import DeleteIcon from '@mui/icons-material/DeleteForever';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import type { GridSize } from '@mui/material/Grid';
import Grid from '@mui/material/Grid';
import IconButton from '@mui/material/IconButton';
import Snackbar from '@mui/material/Snackbar';
import Typography from '@mui/material/Typography';
import type { Breakpoint } from '@mui/material/styles';
import debounce from 'debounce';
import { Component, forwardRef } from 'react';
import { makeStyles } from 'tss-react/mui';

import { getAllDimensionTypes } from '~/api/dimensions';
import * as api from '~/api/dimensions';
import { DimensionsService } from '~/services/dimensions';
import type { ApiTimeDimension, DimensionType, DiscreteDimension } from '~/types/api-types';
import { DataStatus } from '~/types/types';

// FIXME checkout https://mui.com/components/use-media-query/#migrating-from-withwidth
const withWidth = () => (WrappedComponent: any) => (props: any) => {
  return <WrappedComponent {...props} width="xs" />;
};

const useStyles = makeStyles()({
  suggestionsDataItem: {
    '&:not(:last-child)': {
      padding: '8px 5px',
      borderBottom: '1px solid rgba(128, 128, 128, 0.3)',
    },
  },

  tableHeadRow: {
    marginTop: '20px',
  },

  deleteButton: {
    padding: '5px',
  },
});

export interface DimensionsListProps {
  readonly currentSuggestions: {
    totalResults: number;
    dimensions: DiscreteDimension[];
    status: DataStatus;
  };
  readonly width: string;
  readonly onDeleteDimension?: (id: number) => void;
  classes: ReturnType<typeof useStyles>['classes'];
}

export interface DimensionsListState {
  readonly deleteConfirmationOpen: boolean;
  readonly deleteConfirmationId: number | null;
  readonly snackbar: { show: boolean; message: string };
}

type Sizes = Partial<Record<Breakpoint, GridSize>>;

class DimensionSuggestionsList extends Component<DimensionsListProps, DimensionsListState> {
  private types: DimensionType[] = [];
  private timeDimensions: ApiTimeDimension[] = [];

  constructor(props: DimensionsListProps) {
    super(props);
    this.state = {
      deleteConfirmationId: null,
      deleteConfirmationOpen: false,
      snackbar: { show: false, message: '' },
    };
  }

  private sizes: { [x: string]: Sizes } = {
    year: { lg: 2, md: 2, sm: 2, xs: 2 },
    type: { lg: 2, md: 2, sm: 2, xs: 3 },
    name: { lg: 2, md: 2, sm: 3, xs: 3 },
    prefix: { lg: 2, md: 2, sm: 2, xs: 3 },
    button: { lg: 2, md: 3, sm: 3, xs: 1 },
  };

  componentDidMount = async () => {
    [this.types, this.timeDimensions] = await Promise.all([
      getAllDimensionTypes(),
      DimensionsService.getSubmissionYears(),
    ]);
  };

  buildTableRow = (dimension: DiscreteDimension) => {
    const { classes } = this.props;

    const type = this.types.find((t) => t.id === dimension.dimensionTypeId);
    const time = this.timeDimensions.find((t) => t.id === dimension.timeDimensionId);
    const prefix = dimension.prefix !== null ? dimension.prefix : '';

    const cell = (child: any, sizes: Sizes) => (
      <Grid item {...sizes} className={classes.suggestionsDataItem}>
        <Typography>{child}</Typography>
      </Grid>
    );
    return (
      <Grid
        key={dimension.discreteDimensionId}
        data-dimensionid={dimension.discreteDimensionId} // Nice to have for debugging, but serves no functional purpose
        container
        item
        xs={12}
        justifyContent="center"
        direction="row"
      >
        {cell(time !== undefined ? time.name : 'None', this.sizes.year)}
        {cell(type !== undefined ? type.name : 'None', this.sizes.type)}
        {cell(dimension.name, this.sizes.name)}
        {cell(prefix, this.sizes.prefix)}
        <Grid item {...this.sizes.button}>
          <IconButton
            className={classes.deleteButton}
            onClick={() =>
              this.setState({
                deleteConfirmationOpen: true,
                deleteConfirmationId: dimension.discreteDimensionId,
              })
            }
            size="large"
          >
            <DeleteIcon titleAccess="Delete" color="action" />
          </IconButton>
        </Grid>
      </Grid>
    );
  };

  buildHead = () => {
    const { classes } = this.props;
    const cell = (child: any, sizes: Sizes) => (
      <Grid item {...sizes} className={classes.suggestionsDataItem}>
        <Typography variant="subtitle2">{child}</Typography>
      </Grid>
    );

    return (
      <Grid container item xs={12} justifyContent="center" className={classes.tableHeadRow}>
        {cell('År', this.sizes.year)}
        {cell('Typ', this.sizes.type)}
        {cell('Namn', this.sizes.name)}
        {cell('Prefix', this.sizes.prefix)}
        {cell('', this.sizes.button)}
      </Grid>
    );
  };

  buildTable = () => {
    const renderHead = this.props.width === 'xs';
    return (
      <>
        <Grid container item xl={12} xs={12}>
          {renderHead && this.buildHead()}
          {this.props.currentSuggestions!.dimensions.map(this.buildTableRow)}
        </Grid>
        <Typography>
          Visar {this.props.currentSuggestions!.dimensions.length} av {this.props.currentSuggestions!.totalResults}
        </Typography>
      </>
    );
  };

  buildConfirmationDialogue = () => {
    return (
      <Dialog open={this.state.deleteConfirmationOpen}>
        <DialogContent>
          <DialogContentText>Ta bort dimensionen?</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => this.setState({ deleteConfirmationOpen: false })} color="secondary">
            Avbryt
          </Button>
          <Button autoFocus={true} color="primary" onClick={this.deleteDimension}>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  // If the user is quick enough they can double-click confirmation button, so debounce this
  private deleteDimension = debounce(async () => {
    const { deleteConfirmationId } = this.state;
    this.setState({ deleteConfirmationOpen: false });
    if (deleteConfirmationId === null) {
      // This probably should happen, but show something just to be sure
      this.setState({
        snackbar: { show: true, message: 'Ingen dimension vald' },
      });
      return;
    }
    try {
      await api.deleteDimension(deleteConfirmationId);
      if (this.props.onDeleteDimension) {
        this.props.onDeleteDimension(deleteConfirmationId);
      }
      this.setState({
        snackbar: { show: true, message: 'Dimensionen har tagits bort!' },
      });
    } catch (error: any) {
      this.setState({ snackbar: { show: true, message: error.message } });
    }
  }, 250);

  buildSuggestionsList = () => {
    if (this.props.currentSuggestions.dimensions.length === 0) {
      return <Typography align="center">Inga förslag att visa</Typography>;
    }
    return (
      <>
        {this.buildTable()}
        {this.buildConfirmationDialogue()}
        <Snackbar
          open={this.state.snackbar.show}
          message={this.state.snackbar.message}
          autoHideDuration={6000}
          onClose={() => this.setState({ snackbar: { show: false, message: '' } })}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        />
      </>
    );
  };

  render = () => {
    switch (this.props.currentSuggestions.status) {
      case DataStatus.Uninitialized:
        return <Typography align="center">Fyll i mer för att visa existerande dimensioner</Typography>;
      case DataStatus.Error:
        return <Typography align="center">Kunde inte hämta förslag</Typography>;
      case DataStatus.Waiting:
        return <Typography align="center">Hämtar resultat...</Typography>;
      default:
        return this.buildSuggestionsList();
    }
  };
}

const Wrapper = forwardRef<DimensionSuggestionsList, Omit<DimensionsListProps, 'classes'>>((props, ref) => {
  const { classes } = useStyles();

  return <DimensionSuggestionsList ref={ref} classes={classes} {...props} />;
});

export default withWidth()(Wrapper);
